import React, { useState } from 'react';
import { ServiceCatalogModal } from '@nile/client_management/src/Contracts/DeliveryMap/ServiceCatalog/ServiceCatalogModal';
import {
  AccountClient,
  ADD_WORKLOG_ENTRIES,
  TicketClient
} from '@nile/graphql';
import {
  EventEmitter,
  DATE_TIME_FORMAT,
  useActivityTracker,
  useCheckFeatureGating
} from '@nile/components';
import { GET_ELIGIBLE_SERVICE_ITEMS } from '@nile/graphql/src/Query/serviceCatalogNew';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { SVGIcon } from '@superops/super-icons';
import { Button } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import { omit } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

export function ModuleBasedServiceCatalog({ closeModal, ticket, project }) {
  const isTicket = !!ticket?.ticketId;
  const flags = useFlags();

  const module = isTicket ? 'TICKET' : 'PROJECT';
  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const [createWorklogEntry] = useMutation(ADD_WORKLOG_ENTRIES, {
    onCompleted() {
      useActivityTracker({
        event: 'Billing - products added'
      });
      EventEmitter.dispatch('refetchProductWorklogs');
    }
  });

  const contractId = isTicket
    ? ticket?.contractTicketMapping?.find((ticketMap) => ticketMap.inUse)
        ?.contract?.contractId
    : project?.contract?.contractId;

  function getVariables(listInfo: any) {
    const lInfo = listInfo?.condition?.operands?.length
      ? listInfo
      : omit(listInfo, 'condition');

    return {
      input: {
        ...(contractId && {
          contract: {
            contractId
          }
        }),
        listInfo: lInfo
      }
    };
  }

  return (
    <ServiceCatalogModal
      isFromTicketOrProject
      open
      closeModal={closeModal}
      onRecordsChange={(records) => {
        const input = records.map((el) => {
          return {
            [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
              itemId: el?.itemId
            },
            billable: true,
            qty: '1',
            billDateTime: dayjs().format(DATE_TIME_FORMAT),
            notes: el?.description || el?.name,
            module,
            workItem:
              ticket?.ticketId || project?.projectId
                ? {
                    workId: ticket?.ticketId || project?.projectId,
                    module
                  }
                : null
          };
        });

        return createWorklogEntry({
          variables: {
            input: { entries: input }
          }
        });
      }}
      accessor={{
        data: (resp: {
          data: { getEligibleServiceCatalogItems: { items: any } };
        }) => resp.data.getEligibleServiceCatalogItems.items,
        listInfo: (resp) => resp.data.getEligibleServiceCatalogItems.listInfo
      }}
      onSelectionQuery={async function(listInfo: any, isNetworkOnly = false) {
        return AccountClient.query({
          query: GET_ELIGIBLE_SERVICE_ITEMS,
          fetchPolicy: isNetworkOnly ? 'network-only' : 'cache-first',
          variables: getVariables(listInfo)
        });
      }}
    />
  );
}

export function ModuleBasedServiceCatalogModal({
  children: activator,
  open,
  ...rest
}) {
  const [showModal, setShowModal] = useState(false);

  function onClose() {
    if (rest.closeModal) {
      return rest.closeModal();
    }

    setShowModal(false);
  }

  return (
    <>
      {activator && (
        <div className="cursor-pointer" onClick={() => setShowModal(true)}>
          {activator}
        </div>
      )}
      {(open ?? showModal) && (
        <ModuleBasedServiceCatalog {...rest} closeModal={onClose} />
      )}
    </>
  );
}

export function ProjectServiceCatalogModal({ project }) {
  return (
    <ModuleBasedServiceCatalogModal project={project}>
      <Button icon={<SVGIcon name="Add" />}>
        <div className="ellipsis">
          <FormattedMessage id="ticket.details.worklog.addProducts" />
        </div>
      </Button>
    </ModuleBasedServiceCatalogModal>
  );
}
